import { useAccess } from '@umijs/max';
import {
  Alert,
  Button,
  Input,
  Modal,
  Pagination,
  Space,
  Spin,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Empty from '@/components/QuietEmpty';
import { formatFollowUpDate } from '@/modules/zy-crm/utils/follow-up';
import { hasActionAccess } from '@/permissions/helpers';
import { deleteFollowUp, queryFollowUps } from '@/services/bff/follow-up.api';
import type { FollowUpItem } from '@/services/bff/types/follow-up';
import { FollowUpCreateModal } from '../follow-up-create-modal';
import { FOLLOW_UP_BIZ_TYPES } from '../markdown/content';
import { LegacyFollowUpFiles } from '../markdown/legacy-files';
import { MarkdownView } from '../markdown/view';
import type { ActivityFollowUpPanelProps } from './types';

export function ActivityPanelFollowUps({
  bizType,
  bizUuid,
  bizName,
  relatedScopes = [],
  onTotalChange,
  onRecordSaved,
}: ActivityFollowUpPanelProps) {
  const access = useAccess() as Record<string, boolean>;
  const globalCreate = hasActionAccess(
    access,
    'collaboration-follow-ups',
    'create',
  );
  const globalEdit = hasActionAccess(
    access,
    'collaboration-follow-ups',
    'edit',
  );
  const globalDelete = hasActionAccess(
    access,
    'collaboration-follow-ups',
    'delete',
  );
  const [records, setRecords] = useState<FollowUpItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [searchText, setSearchText] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FollowUpItem>();
  const callbacks = useRef({ onTotalChange, onRecordSaved });
  callbacks.current = { onTotalChange, onRecordSaved };
  const revision = useRef(0);
  const scopes = JSON.stringify(
    Array.from(
      new Map(
        [{ targetType: bizType, targetUuid: bizUuid }, ...relatedScopes]
          .filter((scope) => scope.targetUuid)
          .map((scope) => [`${scope.targetType}:${scope.targetUuid}`, scope]),
      ).values(),
    ),
  );
  const load = useCallback(async () => {
    const current = ++revision.current;
    if (!bizUuid) return;
    setLoading(true);
    setError(false);
    try {
      const result = await queryFollowUps({
        scopes,
        bizType: FOLLOW_UP_BIZ_TYPES[bizType],
        bizUuid,
        page,
        pageSize: 10,
        keyword,
      });
      if (current !== revision.current) return;
      if (!result.list.length && page > 1) {
        setPage(Math.max(1, Math.ceil(result.total / 10)));
        return;
      }
      setCanCreate(result.permissions?.create ?? globalCreate);
      setRecords(result.list);
      setTotal(result.total);
      callbacks.current.onTotalChange?.(result.total);
    } catch {
      if (current === revision.current) setError(true);
    } finally {
      if (current === revision.current) setLoading(false);
    }
  }, [bizType, bizUuid, scopes, page, keyword, globalCreate]);
  useEffect(() => {
    setPage(1);
    setKeyword('');
    setSearchText('');
    setCanCreate(false);
    setRecords([]);
    setTotal(0);
  }, [bizUuid, scopes]);
  useEffect(() => {
    void load();
    return () => {
      revision.current += 1;
    };
  }, [load]);
  const context = useMemo(
    () => ({
      bizType: editing?.bizType || FOLLOW_UP_BIZ_TYPES[bizType],
      bizUuid: editing?.bizUuid || bizUuid,
      bizName: editing?.bizName || bizName,
      defaultRelations: editing?.relations,
    }),
    [bizType, bizUuid, bizName, editing],
  );
  const saved = async (reset = false) => {
    setModalOpen(false);
    setEditing(undefined);
    if (reset) {
      revision.current += 1;
      setPage(1);
      setKeyword('');
      setSearchText('');
    }
    if (!reset || (page === 1 && !keyword)) await load();
    await callbacks.current.onRecordSaved?.();
  };
  if (!bizUuid)
    return <Empty description="缺少业务对象，暂时无法加载跟进记录" />;
  return (
    <section style={{ minWidth: 0, maxWidth: '100%' }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          aria-label="搜索跟进内容"
          placeholder="搜索跟进内容"
          allowClear
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onSearch={(value) => {
            setKeyword(value);
            setPage(1);
          }}
        />
        <Button onClick={() => void load()}>刷新</Button>
        {canCreate && (
          <Button
            type="primary"
            onClick={() => {
              setEditing(undefined);
              setModalOpen(true);
            }}
          >
            新增跟进
          </Button>
        )}
      </Space>
      {error ? (
        <Alert
          type="error"
          title="跟进记录加载失败"
          action={<Button onClick={() => void load()}>重试</Button>}
        />
      ) : (
        <Spin spinning={loading}>
          {!records.length ? (
            <Empty description={false} />
          ) : (
            records.map((record) => (
              <article
                key={record.uuid}
                style={{
                  marginBottom: 16,
                  padding: 16,
                  borderRadius: 8,
                  background: 'var(--ant-color-fill-alter)',
                }}
              >
                <Space wrap style={{ marginBottom: 8 }}>
                  <Typography.Text type="secondary">
                    {formatFollowUpDate(record)}
                  </Typography.Text>
                  {record.bizName && (
                    <Typography.Text>{record.bizName}</Typography.Text>
                  )}
                  {(record.permissions?.edit ?? globalEdit) && (
                    <Button
                      type="text"
                      size="small"
                      onClick={() => {
                        setEditing(record);
                        setModalOpen(true);
                      }}
                    >
                      编辑
                    </Button>
                  )}
                  {(record.permissions?.delete ?? globalDelete) && (
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={() =>
                        Modal.confirm({
                          closable: { 'aria-label': '关闭' },
                          keyboard: true,
                          title: '删除这条跟进记录？',
                          okText: '删除',
                          cancelText: '取消',
                          onOk: async () => {
                            await deleteFollowUp(record.uuid);
                            await saved();
                          },
                        })
                      }
                    >
                      删除
                    </Button>
                  )}
                </Space>
                <MarkdownView
                  content={record.content}
                  contentFormat={record.contentFormat}
                />
                <LegacyFollowUpFiles record={record} />
                {record.nextStepContent && (
                  <p>下一步：{record.nextStepContent}</p>
                )}
              </article>
            ))
          )}
          <Pagination
            current={page}
            total={total}
            pageSize={10}
            showTotal={(count) => `共 ${count} 条`}
            showSizeChanger={false}
            onChange={setPage}
          />
        </Spin>
      )}
      {modalOpen && (
        <FollowUpCreateModal
          open
          title={editing ? '编辑跟进记录' : '新建跟进记录'}
          context={context}
          editTarget={editing ? { uuid: editing.uuid } : undefined}
          onCancel={() => setModalOpen(false)}
          onSuccess={() => saved(!editing)}
        />
      )}
    </section>
  );
}
